/** The six fields exported by the EBS FND metadata query. */
export type RelationRow = { pk_name: string; pk_table_name: string; pk_column_name: string; fk_name: string; fk_table_name: string; fk_column_name: string };
export const CSV_HEADERS = ["PK_NAME", "PK_TABLE_NAME", "PK_COLUMN_NAME", "FK_NAME", "FK_TABLE_NAME", "FK_COLUMN_NAME"] as const;
const FIELDS = CSV_HEADERS.map(h => h.toLowerCase()) as (keyof RelationRow)[];
export const normalizeTable = (value: string) => value.trim().toUpperCase();
export function validateTable(value: string, optional = false): string {
  const result = normalizeTable(value);
  if (!result && optional) return "";
  if (!result) throw new Error("Enter a primary table name.");
  if (!/^[A-Z][A-Z0-9_$#]{0,127}$/.test(result)) throw new Error("Use a table name without a schema prefix, spaces, or SQL wildcards.");
  return result;
}

/** CSV with RFC-style quoted fields, escaped quotes, UTF-8 BOM, and LF/CRLF. */
export function parseRelationsCsv(raw: string): { rows: RelationRow[]; duplicates: number } {
  const input = raw.replace(/^\uFEFF/, "");
  const records: { values: string[]; line: number }[] = [];
  let row: string[] = [], cell = "", quoted = false, closed = false, line = 1, recordLine = 1;
  const pushCell = () => { row.push(cell.trim()); cell = ""; closed = false; };
  const pushRow = () => { pushCell(); if (row.some(v => v !== "")) records.push({ values: row, line: recordLine }); row = []; };
  for (let i = 0; i < input.length; i++) {
    const c = input[i];
    if (quoted) {
      if (c === '"') { if (input[i + 1] === '"') { cell += '"'; i++; } else { quoted = false; closed = true; } }
      else { cell += c; if (c === "\n") line++; }
      continue;
    }
    if (c === ",") { pushCell(); continue; }
    if (c === "\n" || c === "\r") {
      if (c === "\r" && input[i + 1] === "\n") i++;
      pushRow(); line++; recordLine = line; continue;
    }
    if (closed) { if (!/[ \t]/.test(c)) throw new Error(`Line ${line}: unexpected text after a closing quote.`); continue; }
    if (c === '"') { if (cell.trim()) throw new Error(`Line ${line}: unexpected quote in an unquoted field.`); cell = ""; quoted = true; }
    else cell += c;
  }
  if (quoted) throw new Error(`Line ${recordLine}: a quoted field is not closed.`);
  if (cell || row.length || closed) pushRow();
  if (!records.length) throw new Error("The CSV file is empty.");
  const headers = records[0].values.map(normalizeTable);
  const missing = CSV_HEADERS.filter(h => !headers.includes(h));
  if (missing.length) throw new Error(`Missing CSV columns: ${missing.join(", ")}. The first line must contain the six column names.`);
  if (new Set(headers).size !== headers.length) throw new Error("The CSV header contains duplicate column names.");
  const indexes = CSV_HEADERS.map(h => headers.indexOf(h));
  const rows: RelationRow[] = [], seen = new Set<string>(); let duplicates = 0;
  for (const record of records.slice(1)) {
    if (record.values.length !== headers.length) throw new Error(`Line ${record.line}: expected ${headers.length} fields, found ${record.values.length}. Put each relationship on its own line.`);
    const values = indexes.map(i => normalizeTable(record.values[i]));
    for (let i = 0; i < values.length; i++) {
      if (!values[i]) throw new Error(`Line ${record.line}: ${CSV_HEADERS[i]} is empty.`);
      if (/[\x00-\x1F\x7F]/.test(values[i]) || values[i].length > 128) throw new Error(`Line ${record.line}: ${CSV_HEADERS[i]} must be a single identifier of at most 128 characters.`);
    }
    try { validateTable(values[1]); validateTable(values[4]); } catch { throw new Error(`Line ${record.line}: table names must be unqualified Oracle identifiers.`); }
    const key = JSON.stringify(values);
    if (seen.has(key)) { duplicates++; continue; }
    seen.add(key);
    rows.push(Object.fromEntries(FIELDS.map((field, i) => [field, values[i]])) as RelationRow);
  }
  if (!rows.length) throw new Error("The CSV has a header but no relationship rows.");
  return { rows, duplicates };
}

export type RelationshipDataset = {
  rows: RelationRow[]; byPrimary: Map<string, RelationRow[]>; primaryTables: string[];
  foreignTables: string[]; foreignByPrimary: Map<string, string[]>; knownTables: Set<string>; relationshipCount: number;
};
/** A composite foreign key is one relationship, even when it has several column pairs. */
export const relationshipKey = (row: RelationRow) => JSON.stringify([row.pk_table_name, row.pk_name, row.fk_table_name, row.fk_name]);
export function buildDataset(rows: RelationRow[]): RelationshipDataset {
  const byPrimary = new Map<string, RelationRow[]>(), foreignSets = new Map<string, Set<string>>(), knownTables = new Set<string>(), foreign = new Set<string>(), keys = new Set<string>();
  for (const row of rows) {
    if (!byPrimary.has(row.pk_table_name)) { byPrimary.set(row.pk_table_name, []); foreignSets.set(row.pk_table_name, new Set()); }
    byPrimary.get(row.pk_table_name)!.push(row);
    foreignSets.get(row.pk_table_name)!.add(row.fk_table_name);
    knownTables.add(row.pk_table_name); knownTables.add(row.fk_table_name); foreign.add(row.fk_table_name);
    keys.add(relationshipKey(row));
  }
  return { rows, byPrimary, primaryTables: [...byPrimary.keys()].sort(), foreignTables: [...foreign].sort(), foreignByPrimary: new Map([...foreignSets].map(([key, value]) => [key, [...value].sort()])), knownTables, relationshipCount: keys.size };
}
export function searchTables(tables: string[], query: string, limit = 100) {
  const q = normalizeTable(query);
  const prefix: string[] = [], other: string[] = [];
  for (const table of tables) { if (table.startsWith(q)) prefix.push(table); else if (table.includes(q)) other.push(table); }
  return { matches: [...prefix, ...other].slice(0, limit), total: prefix.length + other.length };
}
export function foreignTableOptions(dataset: RelationshipDataset, primary: string): string[] {
  const name = normalizeTable(primary);
  return dataset.knownTables.has(name) ? (dataset.foreignByPrimary.get(name) ?? []) : dataset.foreignTables;
}
export async function queryRelations(dataset: RelationshipDataset, primaryName: string, foreignName = ""): Promise<RelationRow[]> {
  const primary = validateTable(primaryName), foreign = validateTable(foreignName, true);
  if (!dataset.knownTables.has(primary)) throw new Error(`${primary} is not present in the loaded CSV. Select a matching table from the dropdown.`);
  return (dataset.byPrimary.get(primary) ?? []).filter(r => !foreign || r.fk_table_name === foreign);
}

export type Point = { x: number; y: number };
export type Constraint = { name: string; pkName: string; rows: RelationRow[]; labelLines: string[]; width: number; height: number };
export type NodeDisplay = { nameLines: string[]; columnLines: string[]; glyphY: number; nameY: number; dividerY: number; columnHeadingY: number; columnY: number; hintY: number; radius: number };
export type TableNode = Point & { id: string; table: string; fkColumns: string[]; constraints: Constraint[]; display: NodeDisplay; radius: number };
export type RelationGroup = Point & { id: string; number: number; primary: string; primaryRadius: number; foreignFilter: string; nodes: TableNode[]; rows: RelationRow[]; relationshipCount: number };
export const PRIMARY_RADIUS = 104;

export function wrapIdentifier(name: string, max = 22): string[] {
  const lines: string[] = []; let current = "";
  for (const token of name.match(/[^_]+_?/g) ?? [name]) {
    if (current && current.length + token.length > max) { lines.push(current); current = ""; }
    current += token;
  }
  if (current) lines.push(current);
  return lines.flatMap(line => line.match(new RegExp(`.{1,${max}}`, "g")) ?? [line]);
}
function nodeDisplay(table: string, columns: string[]): NodeDisplay {
  const nameLines = wrapIdentifier(table, 24), columnLines = columns.flatMap(c => wrapIdentifier(c, 32));
  const nameY = 38, dividerY = nameY + (nameLines.length - 1) * 21 + 14, columnHeadingY = dividerY + 20;
  const columnY = columnHeadingY + 21, hintY = columnY + (columnLines.length - 1) * 19 + 30, center = hintY / 2;
  const extents = [Math.abs(center) + 25, Math.abs(hintY - center) + 20,
    ...nameLines.map((line, i) => Math.hypot(line.length * 4.5, nameY + i * 21 - center - 5) + 22),
    ...columnLines.map((line, i) => Math.hypot(line.length * 4.2, columnY + i * 19 - center - 5) + 22)];
  return { nameLines, columnLines, glyphY: -center, nameY: nameY - center, dividerY: dividerY - center, columnHeadingY: columnHeadingY - center, columnY: columnY - center, hintY: hintY - center, radius: Math.ceil(Math.max(126, ...extents)) };
}
/** Exclude relationships already drawn anywhere, independent of a group's filter or origin. */
export function newCanvasRelations(groups: RelationGroup[], rows: RelationRow[]) {
  const requested = new Set(rows.map(relationshipKey));
  const existingGroups = groups.filter(g => g.rows.some(row => requested.has(relationshipKey(row))));
  const present = new Set(existingGroups.flatMap(g => g.rows.map(relationshipKey)));
  return {
    rows: rows.filter(row => !present.has(relationshipKey(row))),
    existingGroups,
    skippedCount: [...requested].filter(key => present.has(key)).length,
  };
}
function createNodes(number: number, data: RelationRow[]): TableNode[] {
  const byTable = new Map<string, RelationRow[]>();
  for (const row of data) { if (!byTable.has(row.fk_table_name)) byTable.set(row.fk_table_name, []); byTable.get(row.fk_table_name)!.push(row); }
  return [...byTable].map(([table, rows], i) => {
    const fkColumns = [...new Set(rows.map(r => r.fk_column_name))], grouped = new Map<string, RelationRow[]>();
    for (const row of rows) { const key = JSON.stringify([row.pk_name, row.fk_name]); if (!grouped.has(key)) grouped.set(key, []); grouped.get(key)!.push(row); }
    const constraints = [...grouped.values()].map(rows => {
      const labelLines = rows.flatMap(r => [...wrapIdentifier(r.pk_column_name, 32).map((s, i) => `${i ? "    " : "PK: "}${s}`), ...wrapIdentifier(r.fk_column_name, 32).map((s, i) => `${i ? "    " : "FK: "}${s}`)]);
      return { name: rows[0].fk_name, pkName: rows[0].pk_name, rows, labelLines, width: Math.max(...labelLines.map(s => s.length)) * 7.8 + 24, height: labelLines.length * 17 + 18 };
    });
    const display = nodeDisplay(table, fkColumns);
    return { id: `group-${number}-node-${i}`, table, fkColumns, constraints, display, radius: display.radius, x: 0, y: 0 };
  });
}
export function radialOffsets(nodes: TableNode[], primaryRadius = PRIMARY_RADIUS): Point[] {
  const count = nodes.length; if (!count) return [];
  const maxRadius = nodes.reduce((m, n) => Math.max(m, n.radius), 0);
  const maxLabel = nodes.reduce((m, n) => Math.max(m, ...n.constraints.map(c => c.width)), 0);
  const radius = Math.max(420, primaryRadius + maxRadius + maxLabel + 110, count > 2 ? (maxRadius + 45) / Math.sin(Math.PI / count) : 0);
  if (count === 1) return [{ x: radius, y: 0 }];
  if (count === 2) return [{ x: -radius, y: 0 }, { x: radius, y: 0 }];
  return nodes.map((_, i) => { const a = count === 4 ? -Math.PI * .75 + i * Math.PI / 2 : -Math.PI / 2 + i * 2 * Math.PI / count; return { x: Math.cos(a) * radius, y: Math.sin(a) * radius }; });
}
export function createGroup(number: number, primary: string, foreignFilter: string, data: RelationRow[], center: Point): RelationGroup {
  const nodes = createNodes(number, data), primaryRadius = PRIMARY_RADIUS + Math.max(0, wrapIdentifier(primary, 14).length - 3) * 12;
  const offsets = radialOffsets(nodes, primaryRadius);
  return { id: `group-${number}`, number, primary, primaryRadius, foreignFilter, ...center, rows: data, nodes: nodes.map((node, i) => ({ ...node, ...offsets[i] })), relationshipCount: nodes.reduce((s, n) => s + n.constraints.length, 0) };
}
export function moveNode(groups: RelationGroup[], groupId: string, nodeId: string | undefined, point: Point): RelationGroup[] {
  return groups.map(g => g.id !== groupId ? g : nodeId ? { ...g, nodes: g.nodes.map(n => n.id === nodeId ? { ...n, ...point } : n) } : { ...g, ...point });
}
export function edgeGeometry(group: RelationGroup, node: TableNode, index: number) {
  const d = Math.hypot(node.x, node.y) || 1, ux = node.x / d, uy = node.y / d;
  const totalHeight = node.constraints.reduce((sum, c) => sum + c.height + 12, 0);
  const before = node.constraints.slice(0, index).reduce((sum, c) => sum + c.height + 12, 0);
  const offset = before + (node.constraints[index].height + 12) / 2 - totalHeight / 2;
  const control = { x: node.x / 2 - uy * offset * 2, y: node.y / 2 + ux * offset * 2 };
  const sd = Math.hypot(control.x, control.y) || 1, ed = Math.hypot(node.x - control.x, node.y - control.y) || 1;
  const start = { x: control.x / sd * (group.primaryRadius + 5), y: control.y / sd * (group.primaryRadius + 5) };
  const end = { x: node.x - (node.x - control.x) / ed * (node.radius + 3), y: node.y - (node.y - control.y) / ed * (node.radius + 3) };
  return { start, end, label: { x: (start.x + 2 * control.x + end.x) / 4, y: (start.y + 2 * control.y + end.y) / 4 }, path: `M${start.x},${start.y} Q${control.x},${control.y} ${end.x},${end.y}` };
}
export function boundsFor(groups: RelationGroup[]) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  const include = (x: number, y: number, w: number, h = w) => { minX = Math.min(minX, x - w); maxX = Math.max(maxX, x + w); minY = Math.min(minY, y - h); maxY = Math.max(maxY, y + h); };
  for (const g of groups) {
    include(g.x, g.y, g.primaryRadius + 22);
    for (const n of g.nodes) { include(g.x + n.x, g.y + n.y, n.radius + 10); n.constraints.forEach((c, i) => { const p = edgeGeometry(g, n, i).label; include(g.x + p.x, g.y + p.y, c.width / 2 + 6, c.height / 2 + 6); }); }
  }
  return groups.length ? { minX, maxX, minY, maxY } : { minX: -300, maxX: 300, minY: -220, maxY: 220 };
}
